import type { PaginateResponse } from '@/utils/paginate'
import type { FeedComment, FeedCreateCommentPayload } from '@/types/feed.type'

import { z } from 'zod'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { Card } from '@/components/ui/card'
import { uploadFile } from '@/lib/firebase'
import axios, { endpoints } from '@/lib/axios'
import { useBoolean } from '@/hooks/use-boolean'
import { Skeleton } from '@/components/ui/skeleton'
import { MEDIA_LIMIT } from '@/constant/media-limit'
import { zodResolver } from '@hookform/resolvers/zod'
import { useDeepEffect } from '@/hooks/use-deep-effect'
import InfiniteScroll from 'react-infinite-scroll-component'
import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useAuthContext } from '@/contexts/auth-context/auth-provider'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Dialog, DialogTrigger, DialogContent } from '@/components/ui/dialog'

import MainComment from './feed-comment-main-content'
import CommentFormDialog from './feed-comment-form-dialog'

const { MAX_IMAGE_SIZE, MAX_VIDEO_SIZE } = MEDIA_LIMIT

export const commentSchema = z.object({
  content: z.string().min(1, 'กรุณาใส่ข้อความ'),
  parentCommentId: z.string().optional(),
  media: z
    .object({
      type: z.enum(['image', 'gif', 'sticker']),
      url: z.string().url(),
      width: z.number(),
      height: z.number(),
    })
    .optional(),
  taggedUsers: z
    .array(
      z.object({
        userId: z.string(),
        displayName: z.string(),
      }),
    )
    .optional(),
})

type CommentFormValues = z.infer<typeof commentSchema>

export type Props = {
  feedId?: string
  handleUpdate: VoidFunction
}

export const FeedCommentSection: React.FC<Props> = ({
  feedId,
  handleUpdate,
}) => {
  const [comments, setComments] = useState<FeedComment[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [cooldown, setCooldown] = useState(0)
  const [mediaPreview, setMediaPreview] = useState<{
    image: string | null
    video: string | null
  }>({ image: null, video: null })
  const [mediaType, setMediaType] = useState<'image' | 'video'>('image')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const isLoading = useBoolean(false)
  const isLoadingMore = useBoolean(false)
  const isSubmitting = useBoolean(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const form = useForm<CommentFormValues>({
    resolver: zodResolver(commentSchema),
    defaultValues: {
      content: '',
      taggedUsers: [],
    },
  })

  const { user } = useAuthContext()

  const { imageUrl, displayName, firstName, lastName, email, role } = user!
  const name =
    firstName && lastName ? `${firstName} ${lastName}` : displayName || email

  // จัดกลุ่ม comments เป็น parent และ child
  const groupedComments = useMemo(() => {
    const parentComments = comments.filter(
      (comment) => !comment.parentCommentId,
    )
    const childComments = comments.filter((comment) => comment.parentCommentId)

    return parentComments.map((parent) => ({
      parent,
      replies: childComments.filter(
        (child) => child.parentCommentId === parent.id,
      ),
    }))
  }, [comments])

  const fetchComments = async (
    pageNumber: number = 1,
    append: boolean = false,
    skipLoading: boolean = false,
    currentLimitItem?: number,
  ) => {
    if (!feedId) return

    if (!skipLoading) {
      if (append) {
        isLoadingMore.onTrue()
      } else {
        isLoading.onTrue()
      }
    }

    try {
      const response = await axios.get<PaginateResponse<FeedComment>>(
        endpoints.feeds.commentById(feedId),
        {
          params: {
            page: pageNumber,
            limit: currentLimitItem || 10,
            orderBy: 'createdAt:desc',
          },
        },
      )

      const { data } = response

      if (append) {
        setComments((prev) => [...prev, ...data.items])
      } else {
        setComments(data.items)
      }

      setTotal(data.total)
    } catch (error) {
      console.error('Error fetching comments:', error)
      toast.error('ไม่สามารถโหลดความคิดเห็นได้')
    } finally {
      if (!skipLoading) {
        if (append) {
          isLoadingMore.onFalse()
        } else {
          isLoading.onFalse()
        }
      }
    }
  }

  const fetchMoreData = () => {
    if (!isLoading.value && !isLoadingMore.value && comments.length !== total) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchComments(nextPage, true)
    }
  }

  // Cooldown timer for spam prevention
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [cooldown])

  useDeepEffect(() => {
    if (feedId) {
      fetchComments(1, false)
    }
  }, [feedId])

  // Handle file upload
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const maxSize = mediaType === 'image' ? MAX_IMAGE_SIZE : MAX_VIDEO_SIZE
    const acceptType = mediaType === 'image' ? /^image\// : /^video\//

    if (!acceptType.test(file.type)) {
      toast.error(
        `ไฟล์ที่เลือกไม่ใช่${mediaType === 'image' ? 'รูปภาพ' : 'วิดีโอ'}`,
      )
      return
    }

    if (file.size > maxSize) {
      toast.error(
        `ขนาดไฟล์ต้องไม่เกิน ${mediaType === 'image' ? '5MB' : '50MB'}`,
      )
      return
    }

    setSelectedFile(file)

    if (mediaType === 'video') {
      // Generate thumbnail for video
      const url = URL.createObjectURL(file)
      const getThumbnail = (videoUrl: string): Promise<string> =>
        new Promise((resolve) => {
          const video = document.createElement('video')
          video.src = videoUrl
          video.crossOrigin = 'anonymous'
          video.currentTime = 0.1
          video.muted = true
          video.playsInline = true
          video.addEventListener('loadeddata', () => {
            const canvas = document.createElement('canvas')
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            const ctx = canvas.getContext('2d')
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
              resolve(canvas.toDataURL('image/png'))
            } else {
              resolve('')
            }
          })
          video.addEventListener('error', () => resolve(''))
        })

      const thumbnail = await getThumbnail(url)
      setMediaPreview({ image: null, video: thumbnail })
    } else {
      const url = URL.createObjectURL(file)
      setMediaPreview({ image: url, video: null })
    }
  }

  const removeMedia = () => {
    setSelectedFile(null)
    setMediaPreview({ image: null, video: null })
    form.setValue('media', undefined)
  }

  const onSubmit = async (values: CommentFormValues) => {
    if (!feedId) return

    // Check cooldown
    if (cooldown > 0) {
      toast.error(`กรุณารอ ${cooldown} วินาทีก่อนส่งความคิดเห็นใหม่`)
      return
    }

    isSubmitting.onTrue()
    try {
      const payload: Partial<FeedCreateCommentPayload> = {
        content: values.content,
        taggedUsers: values.taggedUsers || [],
      }

      // Add parentCommentId if it's a reply
      if (replyData) {
        payload.parentCommentId = replyData.id
      }

      // Handle media upload
      if (selectedFile) {
        const imageUrl = await uploadFile(
          selectedFile,
          'feed-comments',
          `${selectedFile.name || Math.floor(Math.random() * 1000000)}-${Date.now()}`,
        )

        payload.media = {
          type: mediaType,
          url: imageUrl,
        }
      }

      await axios.post(endpoints.feeds.commentById(feedId), payload)

      // Reset form
      form.reset()
      removeMedia()

      // Set cooldown (5 seconds)
      setCooldown(5)

      // Fetch comments again to get the latest data without showing loading
      await fetchComments(1, false, true, comments.length + 1)

      handleUpdate()
      toast.success('เพิ่มความคิดเห็นสำเร็จ')
      setReplyData(null)
    } catch (error) {
      console.error('Error creating comment:', error)
      toast.error('ไม่สามารถเพิ่มความคิดเห็นได้')
    } finally {
      isSubmitting.onFalse()
    }
  }

  // modal state
  const [open, setOpen] = useState(false)

  const [replyData, setReplyData] = useState<FeedComment | null>(null)

  // state สำหรับ lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxMedia, setLightboxMedia] = useState<{
    url: string
    type: 'image' | 'gif' | 'sticker' | 'video'
  } | null>(null)

  if (isLoading.value) {
    // Loading UI: Skeleton แบบ comment หลายบล็อก
    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-1">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-4 w-8 -mt-2" />
        </div>
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-lg space-y-3 border border-gray-200 shadow-sm"
          >
            <div className="flex items-start space-x-3 p-4">
              <Skeleton className="w-10 h-10 rounded-full" />
              <div className="flex-1 min-w-0 space-y-2">
                <Skeleton className="h-4 w-24 mb-1" />
                <Skeleton className="h-3 w-20 mb-2" />
                <Skeleton className="h-4 w-full max-w-xs mb-2" />
                <Skeleton className="h-32 w-32 rounded-lg" />
                <div className="flex items-center space-x-4 pt-2">
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-4 w-12" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-1">
        <h2 className="text-2xl font-bold text-gray-900">ความคิดเห็น</h2>
        <span className="text-sm text-gray-500 -mt-2">({total})</span>
      </div>
      {/* ปุ่มเปิด modal */}
      <Dialog
        open={open}
        onOpenChange={(e) => {
          setOpen(e)

          if (!e) {
            setReplyData(null)
          }
        }}
      >
        <DialogTrigger asChild>
          {/* Card Trigger แบบ Social */}
          <Card className="p-4 flex flex-row bg-white border border-gray-200 items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors shadow-sm">
            <div className="w-10 h-10 rounded-full flex items-center justify-center">
              <Avatar className="size-12 ring-2 border-2 border-transparent ring-blue-500">
                <AvatarImage
                  src={role !== 'ADMIN' ? imageUrl : '/images/rfc-logo.png'}
                  alt={name || ''}
                  className="object-cover"
                />
                {!!name && (
                  <AvatarFallback className="size-12 bg-gray-100 text-gray-600">
                    {role !== 'ADMIN' ? name.slice(0, 2) || '' : 'RFC'}
                  </AvatarFallback>
                )}
              </Avatar>
            </div>
            <div className="bg-transparent border-2 border-gray-300 h-10 rounded-lg w-full shrink-[20] flex items-center px-4 text-start text-gray-600 placeholder-gray-500">
              {replyData
                ? `ตอบกลับ ${replyData.user.displayName || replyData.user.email}...`
                : 'แสดงความคิดเห็น...'}
            </div>
          </Card>
        </DialogTrigger>
        <CommentFormDialog
          form={form}
          onSubmit={async (values) => {
            await onSubmit(values)
            setOpen(false)
          }}
          isSubmitting={isSubmitting}
          cooldown={cooldown}
          mediaType={mediaType}
          setMediaType={setMediaType}
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
          handleFileChange={handleFileChange}
          mediaPreview={mediaPreview}
          replyData={replyData}
          removeMedia={removeMedia}
        />
      </Dialog>

      {/* Lightbox Dialog */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent
          aria-describedby=""
          className="max-w-2xl w-full flex flex-col items-center justify-center bg-white border border-gray-200"
        >
          {lightboxMedia && (
            <div className="w-full flex flex-col items-center">
              {lightboxMedia.type === 'image' ||
              lightboxMedia.type === 'gif' ||
              lightboxMedia.type === 'sticker' ? (
                <img
                  src={lightboxMedia.url}
                  alt="media preview"
                  className="max-h-[80vh] w-auto rounded-lg object-contain"
                />
              ) : (
                <video
                  src={lightboxMedia.url}
                  controls
                  autoPlay
                  className="max-h-[80vh] w-auto rounded-lg object-contain bg-gray-100"
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Comment list section with grouped replies */}
      <InfiniteScroll
        dataLength={comments.length}
        next={fetchMoreData}
        hasMore={comments.length !== total}
        loader={
          <div className="text-center py-4">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              <p className="text-xs text-gray-500">
                กำลังโหลดความคิดเห็นเพิ่มเติม...
              </p>
            </div>
          </div>
        }
        endMessage={
          <div className="text-center py-4 mt-6">
            <p className="text-xs text-gray-400">•</p>
            <p className="text-xs text-gray-400 mb-2">•</p>
            <p className="text-xs text-gray-500">
              {comments.length === 0
                ? 'ยังไม่มีความคิดเห็น'
                : 'ไม่มีความคิดเห็นเพิ่มเติมแล้ว'}
            </p>
          </div>
        }
      >
        <div className="space-y-4">
          {groupedComments.map(({ parent, replies }) => (
            <MainComment
              key={parent.id}
              comment={parent}
              replies={replies}
              onReply={(comment) => {
                setOpen(true)
                setReplyData(comment)
              }}
              onMediaClick={(media) => {
                setLightboxMedia(media)
                setLightboxOpen(true)
              }}
            />
          ))}
        </div>
      </InfiniteScroll>
    </div>
  )
}
